import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthContext } from '../../hooks/useAuthContext';
import roles from '../../data/roles'; // Import roles with multilingual labels
import { useSnackbar } from 'notistack'; // Import enqueueSnackbar
import i18n from '../../i18n'; // Import i18n for language detection
import { useTranslation } from 'react-i18next'; // Import translation hook
import '../../style/OrganizationInvitationPage.css';

const OrganizationInvitationPage = () => {
    const { orgId } = useParams(); // Get the organization ID from the URL
    const { user } = useAuthContext(); // Get the authenticated user
    const { enqueueSnackbar } = useSnackbar(); // Initialize enqueueSnackbar
    const navigate = useNavigate(); // Initialize useNavigate
    const { t } = useTranslation(); // Translation function
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('owner'); // Default role is "owner"
    const [loading, setLoading] = useState(false);

    const handleInvite = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/organizations/${orgId}/invite`,
                { email, role: role.toLowerCase() }, // Send email and role in the request body
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            enqueueSnackbar(response.data.message || t('organizationInvitationPage.successMessage'), { variant: 'success' });
            setEmail(''); // Clear the email input
            navigate('/'); // Redirect to Home.js after success
        } catch (err) {
            enqueueSnackbar(err.response?.data?.error || t('organizationInvitationPage.errorMessage'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="invitation-page bg-dafauna-background rounded-xl shadow-md p-8">
            <h2 className="text-dafauna-darkText text-2xl font-bold mb-4">{t('organizationInvitationPage.title')}</h2>
            <form onSubmit={handleInvite} className="space-y-4">
                <div>
                    <label htmlFor="email" className="block text-dafauna-darkText font-medium">
                        {t('organizationInvitationPage.emailLabel')}
                    </label>
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('organizationInvitationPage.emailPlaceholder')}
                        required
                        className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    />
                </div>
                <div>
                    <label htmlFor="role" className="block text-dafauna-darkText font-medium">
                        {t('organizationInvitationPage.roleLabel')}
                    </label>
                    <select
                        id="role"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    >
                        {roles.map((r) => (
                            <option key={r.value} value={r.value}>
                                {r.label[i18n.language]} {/* Use the label from roles.js */}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                    {loading ? t('organizationInvitationPage.inviting') : t('organizationInvitationPage.inviteButton')}
                </button>
            </form>
        </div>
    );
};

export default OrganizationInvitationPage;